import { SampleMerge } from '../../models/sample-merge.model';

/**
 * What the merge panel shows, independent of which screen opened it.
 *
 * Lab Manager and Recommendation Manager describe a merge with different
 * payloads, from different APIs, built at different times. Normalising them
 * onto one shape here is what stops the two screens quietly disagreeing about
 * what a merge is.
 */
export interface MergePanelGroup {
  /** The primary sample's name, used as the panel subtitle. */
  primaryName: string;

  /** The primary first, then everything folded into it. Never empty. */
  members: MergePanelMember[];

  /** Every field the group touches, de-duplicated, the primary's field first. */
  fieldNames: string[];

  /** The group spans more than one named field. */
  crossField: boolean;

  /**
   * What this merge means on the screen that opened the panel.
   *
   * Different per screen on purpose. In Lab Manager a merge decides what the
   * lab physically receives; in Recommendation Manager it decides how many
   * recommendations come out. Saying one thing in both places would be wrong
   * in one of them.
   */
  explanation: string;
}

/** One sample in a group, the primary included. */
export interface MergePanelMember {
  sampleId: number | null;
  sampleName: string;
  fieldName: string | null;
  farmName: string | null;
  isPrimary: boolean;
}

const LAB_EXPLANATION =
  'Only the primary sample is sent to the lab. When the results come back they are copied onto ' +
  'every sample below it, so all of them carry the same measurements.';

const RECOMMENDATION_EXPLANATION =
  'These samples produce one recommendation, built from the primary. Selecting any of them, or ' +
  'all of them, gives the same single result.';

/**
 * A Lab Manager merge group, as the merge-groups endpoint returns it.
 *
 * The primary is passed separately because it is a row in the table and the
 * group payload does not carry its field. Linked samples are excluded from the
 * list entirely, which is why they can only be seen here.
 */
export function labMergePanelGroup(
  group: LabMergeGroup | null | undefined,
  primary: { fieldName?: string | null; farmName?: string | null; sampleId?: number | null } | null | undefined,
): MergePanelGroup | null {
  const linked = linkedMembersOf(group);
  if (!linked.length) {
    return null;
  }

  const members: MergePanelMember[] = [
    {
      sampleId: primary?.sampleId ?? null,
      sampleName: group?.primarySampleName ?? 'Primary sample',
      fieldName: primary?.fieldName ?? null,
      farmName: primary?.farmName ?? null,
      isPrimary: true,
    },
    ...linked,
  ];

  return build(members, LAB_EXPLANATION);
}

/**
 * The linked samples, named where the API named them.
 *
 * linkedSamples is the newer field. If this build is deployed ahead of the API
 * that adds it, only linkedSampleIds arrives, and falling back to numbered
 * placeholders keeps the panel opening. An id is poor, which is why the API
 * change exists, but a badge that visibly does nothing when clicked is worse:
 * it reads as a broken screen rather than as a screen waiting on a deploy.
 */
function linkedMembersOf(group: LabMergeGroup | null | undefined): MergePanelMember[] {
  if (group?.linkedSamples?.length) {
    return group.linkedSamples.map(member => ({
      sampleId: member.sampleId ?? null,
      sampleName: member.sampleName ?? 'Unnamed sample',
      fieldName: member.fieldName ?? null,
      farmName: member.farmName ?? null,
      isPrimary: false,
    }));
  }

  return (group?.linkedSampleIds ?? []).map(id => ({
    sampleId: id ?? null,
    sampleName: 'Sample ' + id,
    fieldName: null,
    farmName: null,
    isPrimary: false,
  }));
}

/** The merge group shape the Lab Manager API sends. */
export interface LabMergeGroup {
  primarySampleName?: string | null;
  linkedSampleIds?: number[] | null;
  linkedSamples?: LabLinkedSample[] | null;
}

/** The linked sample shape the Lab Manager API sends. */
export interface LabLinkedSample {
  sampleId?: number | null;
  sampleName?: string | null;
  fieldName?: string | null;
  farmName?: string | null;
}

/**
 * A group as seen from the create-recommendation picker.
 *
 * A merged group is one row there, its primary, so the row handed in is always
 * the primary and its payload always holds the whole group. The members are not
 * on the table at all: that is what this panel exists to show.
 *
 * Earlier this took the whole table as well, because members were rows and a
 * member knew its primary by name but not its siblings. Nothing needs that now,
 * and the member farm comes from the API rather than from a row that no longer
 * exists.
 */
export function recommendationMergePanelGroup(
  row: PickerRow | null | undefined,
): MergePanelGroup | null {
  const merge = row?.merge;
  if (!merge?.members?.length) {
    return null;
  }

  const members: MergePanelMember[] = [
    {
      sampleId: numberOrNull(row.pinSampleId),
      sampleName: row.sampleName ?? 'Primary sample',
      fieldName: row.fieldName ?? null,
      farmName: row.farmName ?? null,
      isPrimary: true,
    },
    ...merge.members.map(member => ({
      sampleId: member.pinSampleId ?? null,
      sampleName: member.sampleName ?? 'Unnamed sample',
      fieldName: member.fieldName ?? null,
      farmName: member.farmName ?? null,
      isPrimary: false,
    })),
  ];

  return build(members, RECOMMENDATION_EXPLANATION);
}

/** A picker row, named loosely: soil and soft tissue rows do not share an interface. */
export interface PickerRow {
  pinSampleId?: string | number | null;
  sampleName?: string | null;
  fieldName?: string | null;
  farmName?: string | null;
  merge?: SampleMerge | null;
}

function numberOrNull(value: string | number | null | undefined): number | null {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

/** Fields are derived from the members rather than trusted, so they cannot disagree. */
function build(members: MergePanelMember[], explanation: string): MergePanelGroup {
  const fieldNames: string[] = [];
  for (const member of members) {
    if (member.fieldName && !fieldNames.includes(member.fieldName)) {
      fieldNames.push(member.fieldName);
    }
  }

  return {
    primaryName: members[0].sampleName,
    members,
    fieldNames,
    crossField: fieldNames.length > 1,
    explanation,
  };
}
